import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Smile, Moon, Activity, Sparkles, Apple, Plus, Scale } from "lucide-react";
import LogSheets from "../components/LogSheets";
import HydrationTracker from "../components/HydrationTracker";
import BreathingSheet, { BreathingStudio } from "../components/BreathingSheet";
import { listMood, listSleep, listFitness, listSkin, listNutrition, listWeight, logWeight } from "../lib/api";
import { fmtDate } from "../lib/format";
import { motion } from "framer-motion";
import { StarSticker, HeartSticker, CrescentSticker, FlowerSticker, SparkBurst, RingSticker, BlobSticker } from "../components/DecorativeShapes";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const TAB_THEME = {
  mood:      { hero: "card-soft-pink", chip: "chip-pink",   Icon: Smile,    Sticker: HeartSticker,   color: "#FF8FB8", title: "How is today landing?", body: "A quick mood check-in helps Knya tailor your insights." },
  sleep:     { hero: "card-lavender",  chip: "chip-lavender",Icon: Moon,     Sticker: CrescentSticker,color: "rgba(255,255,255,0.55)", title: "A calmer morning starts here", body: "Add last night's sleep and we'll surface gentle patterns." },
  fitness:   { hero: "card-butter",    chip: "chip-butter", Icon: Activity, Sticker: SparkBurst,     color: "#F5D867", title: "Move with your phase", body: "Even a 10-minute walk counts. Log what your body enjoyed." },
  skin:      { hero: "card-sky",       chip: "chip-sky",    Icon: Sparkles, Sticker: RingSticker,    color: "rgba(255,255,255,0.7)", title: "Hello, skin", body: "Tracking skin alongside your cycle reveals beautiful patterns." },
  nutrition: { hero: "card-mint",      chip: "chip-mint",   Icon: Apple,    Sticker: FlowerSticker,  color: "rgba(255,255,255,0.55)", title: "Nourish gently", body: "Add a quick meal note. No calorie tracking here." },
};

function HeroEmpty({ tabKey, onLog }) {
  const t = TAB_THEME[tabKey];
  const { Icon, Sticker } = t;
  return (
    <div className={`${t.hero} rounded-[28px] p-5 relative overflow-hidden`} data-testid={`wellness-${tabKey}-empty`}>
      <Sticker size={60} color={t.color} className="absolute -top-2 -right-2 float-slow" />
      <Icon size={22} />
      <p className="font-display text-[26px] leading-tight mt-3" style={{ fontWeight: 600 }}>{t.title}</p>
      <p className="text-sm opacity-80 mt-1.5">{t.body}</p>
      <Button onClick={onLog} className="mt-4 rounded-full bg-white/70 hover:bg-white text-[hsl(var(--foreground))]" data-testid={`wellness-${tabKey}-empty-cta`}>
        <Plus size={16} className="mr-1" /> Log {tabKey}
      </Button>
    </div>
  );
}

function LogRow({ left, right, testid }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[hsl(var(--border))] last:border-0" data-testid={testid}>
      <div className="min-w-0">{left}</div>
      <div className="text-right text-xs text-[hsl(var(--muted-foreground))] max-w-[55%] truncate">{right}</div>
    </div>
  );
}

export default function WellnessPage() {
  const [tab, setTab] = useState("mood");
  const [sheet, setSheet] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [moods, setMoods] = useState([]);
  const [sleeps, setSleeps] = useState([]);
  const [fitness, setFitness] = useState([]);
  const [skin, setSkin] = useState([]);
  const [nutrition, setNutrition] = useState([]);
  const [weights, setWeights] = useState([]);
  const [weightInput, setWeightInput] = useState("");
  const [weightSaving, setWeightSaving] = useState(false);
  const [breathPattern, setBreathPattern] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, s, f, sk, n, w] = await Promise.all([listMood(), listSleep(), listFitness(), listSkin(), listNutrition(), listWeight()]);
        setMoods(m); setSleeps(s); setFitness(f); setSkin(sk); setNutrition(n); setWeights(w);
      } catch {}
    })();
  }, [refresh]);

  const saveWeight = async () => {
    const kg = parseFloat(weightInput);
    if (!kg || isNaN(kg)) return toast.error("Enter a valid weight.");
    setWeightSaving(true);
    try {
      await logWeight({ weight_kg: kg });
      toast.success("Weight logged.");
      setWeightInput("");
      setRefresh((r) => r + 1);
    } catch { toast.error("Could not save weight."); }
    finally { setWeightSaving(false); }
  };

  return (
    <div className="kanya-page-pad-bottom" data-testid="wellness-page">
      <PageHeader eyebrow="Wellness" title="Daily care" subtitle="Mood, sleep, movement, breath & hydration" />

      <div className="px-4 space-y-3">
        {/* Hydration + Breathing — quick-access utility cards */}
        <HydrationTracker />
        <BreathingStudio onSelect={(k) => setBreathPattern(k)} />
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full grid grid-cols-6 h-auto bg-[hsl(var(--card))] p-1.5 rounded-full border border-[hsl(var(--border))]">
            {[
              { v: "mood", I: Smile, tid: "wellness-tab-mood" },
              { v: "sleep", I: Moon, tid: "wellness-tab-sleep" },
              { v: "fitness", I: Activity, tid: "wellness-tab-fitness" },
              { v: "skin", I: Sparkles, tid: "wellness-tab-skin" },
              { v: "nutrition", I: Apple, tid: "wellness-tab-nutrition" },
              { v: "weight", I: Scale, tid: "wellness-tab-weight" },
            ].map(({ v, I, tid }) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={tid}
                className="py-2 rounded-full data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white"
              >
                <I size={16} />
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="mood" className="mt-3 space-y-3">
            <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}>
              <HeroEmpty tabKey="mood" onLog={() => setSheet("mood")} />
            </motion.div>
            {moods.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-mood-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent moods</p>
                {moods.map((m) => (
                  <LogRow key={m.id} testid={`wellness-mood-row-${m.id}`}
                    left={<><p className="capitalize font-semibold">{m.mood}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(m.date)} • Energy {m.energy}/5</p></>}
                    right={m.note || ""}
                  />
                ))}
              </div>
            )}
            <Button className="w-full rounded-full" onClick={() => setSheet("mood")} data-testid="wellness-log-mood-button"><Plus size={16} className="mr-1" />Log mood</Button>
          </TabsContent>

          <TabsContent value="sleep" className="mt-3 space-y-3">
            <HeroEmpty tabKey="sleep" onLog={() => setSheet("sleep")} />
            {sleeps.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-sleep-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent sleep</p>
                {sleeps.map((s) => (
                  <LogRow key={s.id} testid={`wellness-sleep-row-${s.id}`}
                    left={<><p className="font-semibold">{s.hours}h <span className="text-xs text-[hsl(var(--muted-foreground))] font-normal">Quality {s.quality}/5</span></p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(s.date)}</p></>}
                    right={s.note || ""}
                  />
                ))}
              </div>
            )}
            <Button className="w-full rounded-full" onClick={() => setSheet("sleep")} data-testid="wellness-log-sleep-button"><Plus size={16} className="mr-1" />Log sleep</Button>
          </TabsContent>

          <TabsContent value="fitness" className="mt-3 space-y-3">
            <HeroEmpty tabKey="fitness" onLog={() => setSheet("fitness")} />
            {fitness.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-fitness-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent movement</p>
                {fitness.map((f) => (
                  <LogRow key={f.id} testid={`wellness-fitness-row-${f.id}`}
                    left={<><p className="capitalize font-semibold">{f.activity} · {f.duration_minutes}m</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(f.date)} • {f.intensity}</p></>}
                    right={""}
                  />
                ))}
              </div>
            )}
            <Button className="w-full rounded-full" onClick={() => setSheet("fitness")} data-testid="wellness-log-fitness-button"><Plus size={16} className="mr-1" />Log movement</Button>
          </TabsContent>

          <TabsContent value="skin" className="mt-3 space-y-3">
            <HeroEmpty tabKey="skin" onLog={() => setSheet("skin")} />
            {skin.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-skin-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent skin</p>
                {skin.map((s) => (
                  <LogRow key={s.id} testid={`wellness-skin-row-${s.id}`}
                    left={<><p className="capitalize font-semibold">{s.condition}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(s.date)} • Hydration {s.hydration}/5</p></>}
                    right={""}
                  />
                ))}
              </div>
            )}
            <Button className="w-full rounded-full" onClick={() => setSheet("skin")} data-testid="wellness-log-skin-button"><Plus size={16} className="mr-1" />Log skin</Button>
          </TabsContent>

          <TabsContent value="nutrition" className="mt-3 space-y-3">
            <HeroEmpty tabKey="nutrition" onLog={() => setSheet("nutrition")} />
            {nutrition.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-nutrition-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent meals</p>
                {nutrition.map((n) => (
                  <LogRow key={n.id} testid={`wellness-nutrition-row-${n.id}`}
                    left={<><p className="capitalize font-semibold">{n.meal_type}</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(n.date)} • {n.water_glasses || 0} glasses water</p></>}
                    right={n.description}
                  />
                ))}
              </div>
            )}
            <Button className="w-full rounded-full" onClick={() => setSheet("nutrition")} data-testid="wellness-log-nutrition-button"><Plus size={16} className="mr-1" />Log meal</Button>
          </TabsContent>

          <TabsContent value="weight" className="mt-3 space-y-3">
            <div className="card-soft-pink rounded-[28px] p-5 relative overflow-hidden" data-testid="wellness-weight-empty">
              <Scale size={22} />
              <p className="font-display text-[26px] leading-tight mt-3 font-semibold">Body weight</p>
              <p className="text-sm opacity-80 mt-1.5">Track weekly weigh-ins alongside your cycle to spot hormonal patterns.</p>
            </div>
            <div className="bg-[hsl(var(--card))] rounded-[24px] p-4 space-y-3" data-testid="wellness-weight-log-form">
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Log today's weight</p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  min="20"
                  max="300"
                  placeholder="kg (e.g. 62.5)"
                  value={weightInput}
                  onChange={(e) => setWeightInput(e.target.value)}
                  className="flex-1"
                  data-testid="wellness-weight-input"
                />
                <Button onClick={saveWeight} disabled={weightSaving} data-testid="wellness-weight-save-button">
                  {weightSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
            {weights.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="wellness-weight-list">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-2">Recent weigh-ins</p>
                {weights.slice(0, 10).map((w) => (
                  <LogRow key={w.id} testid={`wellness-weight-row-${w.id}`}
                    left={<><p className="font-semibold">{w.weight_kg} kg</p><p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(w.date)}</p></>}
                    right={w.note || ""}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <LogSheets open={sheet} onClose={() => setSheet(null)} onLogged={() => setRefresh((r) => r + 1)} />
      <BreathingSheet open={!!breathPattern} patternKey={breathPattern || "box"} onClose={() => setBreathPattern(null)} />
    </div>
  );
}
